import { ToastContainer } from "react-toastify";
import "./App.css";
import AllRoutes from "./Routes/AllRoutes";
import "react-toastify/dist/ReactToastify.css";
import { useEffect, useState, useCallback } from "react";
import OfflineInitialDataFetch from "./Components/OfflineInitialDataFetch";
import { API_BASE_URL } from "./api";
import { ServerCrash, RefreshCw, AlertTriangle } from "lucide-react";
import { isInitialSetupCompleted, isElectronEnvironment } from "./helper/syncElectron";

function App() {
  document.title = "MediPlus Diagnostics Center";

  const isElectron = isElectronEnvironment();

  const [loading, setLoading] = useState(isElectron);
  const [backendDown, setBackendDown] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [hasAdmins, setHasAdmins] = useState(false);

  const checkBackendHealth = useCallback(async (isManualRetry = false) => {
    if (!isElectron) return;

    setLoading(true);
    setBackendDown(false);
    setErrorMessage("");

    const base = API_BASE_URL || "http://localhost:5000";

    const MAX_ATTEMPTS = isManualRetry ? 1 : 10;
    const BASE_DELAY_MS = 2000;
    const MAX_DELAY_MS = 15000;

    let lastError = null;

    for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
      try {
        // Wait before each attempt (give the backend time to start).
        // On manual retry we skip the initial delay for an instant feel.
        const delay = isManualRetry && attempt === 1
          ? 0
          : Math.min(BASE_DELAY_MS * Math.pow(1.5, attempt - 1), MAX_DELAY_MS);

        if (delay > 0) {
          await new Promise((resolve) => setTimeout(resolve, delay));
        }

        // Health check hits root `/` — always returns 200 without touching MongoDB.
        const response = await fetch(`${base}/`, { method: "GET" });

        console.log(`[App] Health check attempt ${attempt}:`, response.status);

        if (!response.ok) {
          throw new Error(`Server returned HTTP status ${response.status}`);
        }

        // Separately check whether any admins exist.
        // A failure here (e.g. DB still starting) is NOT treated as backend-down.
        try {
          const adminRes = await fetch(`${base}/admin`, {
            method: "GET",
            headers: { "Content-Type": "application/json" },
          });
          if (adminRes.ok) {
            const data = await adminRes.json();
            setHasAdmins(Array.isArray(data) && data.length > 0);
          }
        } catch (_adminErr) {
          console.warn("[App] /admin check failed:", _adminErr.message);
        }

        setBackendDown(false);
        setLoading(false);
        return; // success — exit early
      } catch (error) {
        lastError = error;
        console.warn(`[App] Health check attempt ${attempt}/${MAX_ATTEMPTS} failed:`, error.message);
      }
    }

    // All attempts exhausted — show error screen
    console.error("[App] Backend unreachable after all attempts:", lastError);
    setBackendDown(true);
    setErrorMessage(
      lastError?.message === "Failed to fetch" || lastError?.name === "TypeError"
        ? "Unable to connect to local server at http://localhost:5000. Please verify backend service is running."
        : lastError?.message ?? "Unknown error"
    );
    setLoading(false);
  }, [isElectron]);

  useEffect(() => {
    if (isElectron) {
      checkBackendHealth();
    }
  }, [checkBackendHealth, isElectron]);

  // ON WEB BUILD (NOT ELECTRON): Immediately return AllRoutes with zero health/base-URL checks!
  if (!isElectron) {
    return (
      <>
        <AllRoutes />
        <ToastContainer
          position="top-right"
          autoClose={5000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="light"
        />
      </>
    );
  }

  // ELECTRON DESKTOP APP ONLY
  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-slate-900 text-white p-6">
        <RefreshCw className="w-10 h-10 text-blue-400 animate-spin mb-4" />
        <h2 className="text-lg font-semibold tracking-wide">Connecting to MediPlus Backend...</h2>
        <p className="text-slate-400 text-sm mt-1">Performing initial server health check</p>
      </div>
    );
  }

  if (backendDown) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950 p-6">
        <div className="max-w-md w-full bg-slate-900 border border-slate-800 rounded-3xl p-8 shadow-2xl text-center">
          <div className="mx-auto w-16 h-16 bg-red-500/10 border border-red-500/20 rounded-2xl flex items-center justify-center mb-6">
            <ServerCrash className="w-8 h-8 text-red-500" />
          </div>
          <h2 className="text-2xl font-bold text-white tracking-tight">Backend Server Offline</h2>
          <p className="text-slate-400 text-sm mt-2 leading-relaxed">
            The desktop application cannot reach the local backend service.
          </p>

          <div className="mt-6 p-4 bg-slate-950 border border-red-900/30 rounded-2xl text-left">
            <div className="flex items-start gap-2.5">
              <AlertTriangle className="w-5 h-5 text-red-400 shrink-0 mt-0.5" />
              <div>
                <span className="text-xs font-semibold text-red-400 uppercase tracking-wider block">Error Details</span>
                <p className="text-slate-300 text-xs mt-1 font-mono break-all">{errorMessage}</p>
              </div>
            </div>
          </div>

          <button
            onClick={() => checkBackendHealth(true)}
            className="mt-6 w-full py-3.5 bg-blue-600 hover:bg-blue-500 text-white font-semibold rounded-xl transition-all duration-200 flex items-center justify-center gap-2 shadow-lg shadow-blue-600/25 active:scale-95"
          >
            <RefreshCw className="w-4 h-4" />
            <span>Retry Connection</span>
          </button>
        </div>
      </div>
    );
  }

  const setupCompleted = isInitialSetupCompleted();
  if (!setupCompleted || !hasAdmins) {
    return <OfflineInitialDataFetch />;
  }

  return (
    <>
      <AllRoutes />
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
    </>
  );
}

export default App;
